import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import SEOHelmet from './SEOHelmet';
import { Link } from 'react-router-dom';
import randomShapes from '../assets/random-shapes.svg';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const HomeContainer = styled.div`
  margin: 0;
  padding: 0;
  min-height: 100vh;
  width: 100vw;
  max-width: 100%;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  position: relative;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  padding: 1rem;
  text-align: center;
`;

const HeaderTitle = styled(Link)`
  display: inline-block;
  
  img {
    max-width: 421px;
    height: auto;
  }
`;

const MainContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;

  @media (min-width: 600px) {
    padding: 0 1.5rem;
  }
`;

const WelcomeSection = styled.div`
  text-align: center;
  padding: 2rem 0;
  margin-bottom: 5rem;
  width: 100%;
  overflow: hidden;

  h1 {
    font-size: 3.75rem;
    font-weight: 300;
    line-height: 1.2;
    margin-bottom: 5rem;
    color: var(--text-primary);
    text-shadow: 0 0 20px rgba(157, 78, 221, 0.3);
    max-width: 100%;
    padding: 0 10px;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  .random-shapes {
    width: 50%;
    margin: 3rem auto;
    opacity: 0.8;
    filter: drop-shadow(0 0 10px rgba(157, 78, 221, 0.2));
    
    @media (max-width: 768px) {
      width: 60%;
      margin: 2rem auto;
    }
    
    @media (max-width: 480px) {
      width: 70%;
      margin: 1.5rem auto;
    }
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 400;
    margin-bottom: 1rem;
    color: var(--accent-primary);
  }

  p {
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 5rem;
    color: var(--text-secondary);
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 768px) {
    padding: 1.5rem 0;
    margin-bottom: 3rem;
    
    h1 {
      font-size: 2.5rem;
      margin-bottom: 3rem;
      padding: 0 8px;
    }
    
    h2 {
      font-size: 1.375rem;
    }
    
    p {
      font-size: 0.95rem;
      margin-bottom: 3rem;
      padding: 0 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem 0;
    margin-bottom: 2rem;
    
    h1 {
      font-size: 1.8rem;
      padding: 0 5px;
      margin-bottom: 2rem;
    }
    
    h2 {
      font-size: 1.25rem;
    }
    
    p {
      font-size: 0.9rem;
      margin-bottom: 2rem;
      padding: 0 0.5rem;
    }
  }

  @media (max-width: 333px) {
    h1 {
      font-size: 1.5rem;
      padding: 0 3px;
    }
    
    h2 {
      font-size: 1.125rem;
    }
    
    p {
      font-size: 0.85rem;
    }
  }

  @media (min-width: 600px) {
    padding: 2rem 1.5rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin: 2rem 0;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    margin: 1.5rem 0;
  }
`;

const ValuePropositionSection = styled.div`
  text-align: center;
  padding: 4rem 0;
  margin: 3rem 0;
  background: var(--glass-bg);
  backdrop-filter: blur(8px);
  border: 1px solid var(--glass-border);
  border-radius: 16px;

  h3 {
    font-size: 2.5rem;
    font-weight: 400;
    margin-bottom: 2rem;
    color: var(--accent-primary);
  }

  p {
    font-size: 1.125rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    color: var(--text-secondary);
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }

  @media (max-width: 768px) {
    padding: 2.5rem 1rem;
    margin: 2rem 0;
    
    h3 {
      font-size: 2rem;
    }
    
    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 2rem 1rem;
    
    h3 {
      font-size: 1.75rem;
    }
    
    p {
      font-size: 0.95rem;
    }
  }
`;

const MethodologySection = styled.div`
  padding: 3rem 0;
  margin: 3rem 0;

  h3 {
    font-size: 2.25rem;
    font-weight: 400;
    text-align: center;
    margin-bottom: 3rem;
    color: var(--accent-primary);
  }

  @media (max-width: 768px) {
    padding: 2rem 0;
    margin: 2rem 0;
    
    h3 {
      font-size: 1.875rem;
      margin-bottom: 2rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0;
    
    h3 {
      font-size: 1.625rem;
    }
  }
`;

const MethodologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const MethodologyStep = styled.div`
  text-align: center;
  padding: 2rem;
  border-radius: 12px;
  background: var(--glass-bg);
  backdrop-filter: blur(8px);
  border: 1px solid var(--glass-border);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: var(--accent-primary);
    box-shadow: 0 0 20px rgba(157, 78, 221, 0.2);
  }

  .step-number {
    display: inline-block;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--accent-primary);
    color: white;
    font-weight: 600;
    font-size: 1.25rem;
    line-height: 40px;
    margin-bottom: 1rem;
  }

  h4 {
    font-size: 1.25rem;
    font-weight: 500;
    margin-bottom: 1rem;
    color: var(--text-primary);
  }

  p {
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--text-secondary);
    margin: 0;
  }
`;

const ServiceItem = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  padding: 2rem;
  border-radius: 16px;
  transition: all 0.3s ease;
  background: var(--glass-bg);
  backdrop-filter: blur(8px);
  border: 1px solid var(--glass-border);
  height: 100%;

  &:hover {
    transform: translateY(-5px);
    border-color: var(--accent-primary);
    box-shadow: 0 0 20px rgba(157, 78, 221, 0.2);
  }

  .service-header {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  svg {
    width: 32px;
    height: 32px;
    margin-right: 1rem;
    color: var(--accent-primary);
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  &:hover svg {
    color: var(--accent-secondary);
    transform: scale(1.1);
  }

  h4 {
    font-size: 1.5rem;
    font-weight: 500;
    margin: 0;
    color: var(--text-primary);
  }

  p {
    font-size: 1rem;
    line-height: 1.6;
    color: var(--text-secondary);
    margin: 0;
    flex-grow: 1;
  }

  .service-features {
    margin-top: 1.5rem;
    
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    
    li {
      font-size: 0.9rem;
      color: var(--text-secondary);
      margin-bottom: 0.5rem;
      padding-left: 1.5rem;
      position: relative;
      
      &:before {
        content: '✓';
        position: absolute;
        left: 0;
        color: var(--accent-primary);
        font-weight: bold;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    
    .service-header {
      flex-direction: column;
      align-items: flex-start;
      text-align: center;
      
      svg {
        margin-right: 0;
        margin-bottom: 0.5rem;
      }
    }
    
    h4 {
      font-size: 1.25rem;
    }
    
    p {
      font-size: 0.95rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    
    h4 {
      font-size: 1.125rem;
    }
    
    p {
      font-size: 0.9rem;
    }
    
    .service-features li {
      font-size: 0.85rem;
    }
  }
`;

const ContactSection = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 5rem 0;
  align-items: center;
  padding: 2rem;
  border-radius: 16px;
  background: var(--glass-bg);
  backdrop-filter: blur(8px);
  border: 1px solid var(--glass-border);

  h4 {
    font-size: 2.125rem;
    font-weight: 400;
    margin: 0;
    color: var(--accent-primary);
  }

  p {
    font-size: 1rem;
    line-height: 1.5;
    margin: 0;
    color: var(--text-secondary);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    margin: 3rem 0;
    padding: 1.5rem;
    
    h4 {
      font-size: 1.75rem;
      margin-bottom: 1rem;
    }
    
    p {
      font-size: 0.95rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
    
    h4 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--glass-border);

  p {
    font-size: 0.875rem;
    line-height: 1.43;
    margin: 0;
    color: var(--text-secondary);
  }

  a {
    color: var(--accent-primary);
    text-decoration: none;
    transition: all 0.3s ease;
    
    &:hover {
      color: var(--accent-secondary);
      text-shadow: 0 0 8px rgba(157, 78, 221, 0.3);
    }
  }
`;

function Home() {
  return (
    <>
      <SEOHelmet 
        title="Big Brain - Strategic Technology Consulting"
        description="Transform your business with expert technology consulting. Mobile development, web platforms, UX strategy, and AI solutions that drive measurable growth."
        canonicalUrl="https://bigbrainthings.xyz"
      />
      
      <HomeContainer>
        <MainContent>
          <WelcomeSection>
            <h1>we make apps and such.</h1>
            <img 
              src={randomShapes}
              alt="Random Shapes" 
              className="random-shapes"
            />
            <h2>Transforming Businesses Through Innovation</h2>
            <p>
              Big Brain is a strategic technology consulting firm that partners with organizations 
              to unlock their digital potential. We combine deep technical expertise with business 
              acumen to deliver solutions that drive measurable growth and competitive advantage.
            </p>

            <ServicesGrid>
              <ServiceItem>
                <div className="service-header">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect>
                    <path d="M12 18h.01"></path>
                  </svg>
                  <h4>Mobile Strategy & Development</h4>
                </div>
                <p>
                  End-to-end mobile solutions from strategic planning to App Store deployment. 
                  We help organizations leverage mobile technology to enhance customer engagement 
                  and drive business growth.
                </p>
                <div className="service-features">
                  <ul>
                    <li>iOS & Android native development</li>
                    <li>Cross-platform solutions</li>
                    <li>Mobile UX optimization</li>
                    <li>App Store optimization</li>
                  </ul>
                </div>
              </ServiceItem>

              <ServiceItem>
                <div className="service-header">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"></path>
                    <path d="M2 12h20"></path>
                  </svg>
                  <h4>Digital Platform Consulting</h4>
                </div>
                <p>
                  Comprehensive web platform strategy and development that scales with your business. 
                  From enterprise applications to customer-facing portals, we build robust digital 
                  experiences that deliver results.
                </p>
                <div className="service-features">
                  <ul>
                    <li>Enterprise web applications</li>
                    <li>E-commerce platforms</li>
                    <li>API architecture & integration</li>
                    <li>Performance optimization</li>
                  </ul>
                </div>
              </ServiceItem>

              <ServiceItem>
                <div className="service-header">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"></circle>
                    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor"></circle>
                    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"></circle>
                    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor"></circle>
                    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"></path>
                  </svg>
                  <h4>User Experience Strategy</h4>
                </div>
                <p>
                  Human-centered design consulting that transforms how users interact with your 
                  digital products. We combine research, strategy, and design to create experiences 
                  that drive engagement and conversion.
                </p>
                <div className="service-features">
                  <ul>
                    <li>User research & personas</li>
                    <li>Design system development</li>
                    <li>Conversion optimization</li>
                    <li>Accessibility compliance</li>
                  </ul>
                </div>
              </ServiceItem>

              <ServiceItem>
                <div className="service-header">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"></path>
                    <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"></path>
                    <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4"></path>
                    <path d="M17.599 6.5a3 3 0 0 0 .399-1.375"></path>
                    <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5"></path>
                    <path d="M3.477 10.896a4 4 0 0 1 .585-.396"></path>
                    <path d="M19.938 10.5a4 4 0 0 1 .585.396"></path>
                    <path d="M6 18a4 4 0 0 1-1.967-.516"></path>
                    <path d="M19.967 17.484A4 4 0 0 1 18 18"></path>
                  </svg>
                  <h4>AI & Machine Learning Consulting</h4>
                </div>
                <p>
                  Strategic AI implementation and custom model development that delivers measurable 
                  business value. From LLM fine-tuning to predictive analytics, we help organizations 
                  harness the power of artificial intelligence.
                </p>
                <div className="service-features">
                  <ul>
                    <li>Custom LLM development</li>
                    <li>AI strategy & roadmapping</li>
                    <li>Machine learning pipelines</li>
                    <li>Data science consulting</li>
                  </ul>
                </div>
              </ServiceItem>
            </ServicesGrid>
          </WelcomeSection>

          <ValuePropositionSection>
            <h3>Why Choose Big Brain?</h3>
            <p>
              We don't just build technology—we architect digital transformation. Our consultative 
              approach ensures every solution aligns with your business objectives and delivers 
              measurable ROI. With deep expertise across emerging technologies and proven 
              methodologies, we're your strategic partner for sustainable growth.
            </p>
          </ValuePropositionSection>

          <MethodologySection>
            <h3>Our Proven Methodology</h3>
            <MethodologyGrid>
              <MethodologyStep>
                <div className="step-number">1</div>
                <h4>Discovery & Strategy</h4>
                <p>
                  We begin with comprehensive business analysis and stakeholder interviews 
                  to understand your challenges, goals, and market position.
                </p>
              </MethodologyStep>
              
              <MethodologyStep>
                <div className="step-number">2</div>
                <h4>Solution Architecture</h4>
                <p>
                  Our experts design scalable, future-proof solutions that integrate 
                  seamlessly with your existing systems and processes.
                </p>
              </MethodologyStep>
              
              <MethodologyStep>
                <div className="step-number">3</div>
                <h4>Agile Development</h4>
                <p>
                  We deliver solutions iteratively with continuous feedback loops, 
                  ensuring alignment with your evolving requirements.
                </p>
              </MethodologyStep>
              
              <MethodologyStep>
                <div className="step-number">4</div>
                <h4>Optimization & Growth</h4>
                <p>
                  Post-launch, we provide ongoing optimization, analytics, and strategic 
                  guidance to maximize your technology investment.
                </p>
              </MethodologyStep>
            </MethodologyGrid>
          </MethodologySection>

          <ContactSection>
            <h4>Ready to Transform Your Business?</h4>
            <p>
              Let's discuss how our strategic technology consulting can accelerate your growth. 
              Contact us for a complimentary consultation.
            </p>
            <p style={{ marginTop: '1rem', fontSize: '1.125rem', fontWeight: '500' }}>
              bigbrainthingsandsuch@gmail.com
            </p>
          </ContactSection>

          <Footer>
            <p>
              <Link to="/terms">Terms of Service</Link>
              {' | '}
              <Link to="/privacy">Privacy Policy</Link>
            </p>
          </Footer>
        </MainContent>
      </HomeContainer>
    </>
  );
}

export default Home; 